// Storage key constants
export const StorageKey = {
  RefreshInProg: 'auth:refresh-in-progress',
  AuthState: 'auth:state',
  LogoutIntent: 'auth:logout-intent',
  LastRefresh: 'auth:last-refresh',
  TabId: 'auth:tab-id',
  LastPermissionsRefresh: 'auth:last-permissions-refresh',
  ApiCacheEntries: 'api:cache:entries',
  // todo: categorize by service
  ProductsCacheEntries: 'products:cache:entries',
  OrdersCacheEntries: 'orders:cache:entries',
  QuotesCacheEntries: 'quotes:cache:entries',
  InvoicesCacheEntries: 'invoices:cache:entries',
  MessagesCacheEntries: 'messages:cache:entries',
  CalendarCacheEntries: 'calendar:cache:entries',
  ChatDrafts: 'chat:drafts',
} as const

// Type for valid StorageKey values
export type StorageKeyValue = (typeof StorageKey)[keyof typeof StorageKey]

// TTL values in milliseconds for each storage key
export const StorageKeyTTL = {
  [StorageKey.LogoutIntent]: 0,
  [StorageKey.RefreshInProg]: 60000, // 1 minute
  [StorageKey.AuthState]: 300000, // 5 minutes
  [StorageKey.LastRefresh]: 600000, // 10 minutes
  [StorageKey.TabId]: 0, // No expiration (0 means permanent)
  [StorageKey.LastPermissionsRefresh]: 600000, // 10 minutes
  [StorageKey.ApiCacheEntries]: 0, // No expiration for container (individual entries have their own TTL)
  // todo:
  [StorageKey.ProductsCacheEntries]: 0,
  [StorageKey.OrdersCacheEntries]: 0,
  [StorageKey.QuotesCacheEntries]: 0,
  [StorageKey.InvoicesCacheEntries]: 0,
  [StorageKey.MessagesCacheEntries]: 0,
  [StorageKey.CalendarCacheEntries]: 0,
  [StorageKey.ChatDrafts]: 0, // No expiration for container (drafts are pruned by age and count)
} as const satisfies Record<StorageKeyValue, number>

export const StorageKeyRemoveOnExpire = {
  [StorageKey.LogoutIntent]: false,
  [StorageKey.RefreshInProg]: true,
  [StorageKey.AuthState]: true,
  [StorageKey.LastRefresh]: true,
  [StorageKey.TabId]: false,
  [StorageKey.LastPermissionsRefresh]: false,
  [StorageKey.ApiCacheEntries]: false, // Don't auto-remove container
  [StorageKey.ProductsCacheEntries]: true,
  [StorageKey.OrdersCacheEntries]: true,
  [StorageKey.QuotesCacheEntries]: true,
  [StorageKey.InvoicesCacheEntries]: true,
  [StorageKey.MessagesCacheEntries]: true,
  [StorageKey.CalendarCacheEntries]: true,
  [StorageKey.ChatDrafts]: false, // Don't auto-remove container
} as const satisfies Record<StorageKeyValue, boolean>

/**
 * Gets the TTL for a storage key in milliseconds
 * @param key - Storage key
 * @returns TTL in milliseconds, or undefined if not found
 */
export function getStorageKeyTTL(key: StorageKeyValue): number {
  return StorageKeyTTL[key] ?? 0
}

export function getStorageKeyRemoveOnExpire(key: StorageKeyValue): boolean {
  return StorageKeyRemoveOnExpire[key] ?? false
}
